<template>
  <div class="va-method-pro-viz">
    <div class="viz-header-compact" >
       <div class="brief" v-latex="knowledgePoint"></div>
       <div class="formula-chip" v-latex>$Ω R = U / I$</div>
    </div>

    <div class="split-layout">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage flex-1" style="flex: 1.3;">
        <svg width="100%" height="380" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
           <defs>
              <filter id="glow-va"><feGaussianBlur stdDeviation="3" result="blur" /><feComposite in="SourceGraphic" in2="blur" operator="over" /></filter>
           </defs>

           <!-- Grid Background -->
           <g stroke="rgba(241, 245, 249, 0.03)" stroke-width="2">
              <line v-for="i in 17" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="600" />
              <line v-for="i in 13" :key="'h'+i" x1="0" :y1="i*50" x2="800" :y2="i*50" />
           </g>

           <!-- Circuit Base -->
           <g stroke="#475569" stroke-width="4" fill="none">
              <!-- Source -->
              <path d="M 100 500 V 100 H 700 V 500 H 100" />
              <!-- Components Lines -->
              <path v-if="connection === 'external'" d="M 400 100 V 200" />
              <path v-if="connection === 'internal'" d="M 550 100 V 200" />
           </g>

           <!-- Voltage Source -->
           <g transform="translate(400, 500)">
              <line x1="-30" y1="-10" x2="30" y2="-10" stroke="#f8fafc" stroke-width="6" />
              <line x1="-15" y1="10" x2="15" y2="10" stroke="#f8fafc" stroke-width="4" />
              <text x="50" y="5" fill="#94a3b8" font-size="20">E = 12V</text>
           </g>

           <!-- Slide Rheostat (滑动变阻器) -->
           <g transform="translate(100, 250)">
              <rect x="-15" y="0" width="30" height="150" fill="#1e293b" stroke="#64748b" stroke-width="2" />
              <rect x="-20" :y="150 - (rheostatVal/50)*150 - 10" width="40" height="20" fill="#facc15" rx="2" filter="url(#glow-va)" />
              <text x="-80" y="75" fill="#facc15" font-size="18" font-weight="900" text-anchor="end">变阻器 Rp</text>
           </g>

           <!-- Ammeter (安培表) -->
           <g transform="translate(250, 100)">
              <circle r="35" fill="#0f172a" stroke="#f43f5e" stroke-width="4" filter="url(#glow-va)" />
              <text x="0" y="8" fill="#f43f5e" font-size="32" font-weight="900" text-anchor="middle">A</text>
              <text x="0" y="55" fill="#fca5a5" font-size="16" text-anchor="middle">{{ currentI.toFixed(3) }} A</text>
           </g>

           <!-- Voltmeter (伏特表) -->
           <!-- Positioned based on internal/external method -->
           <g :transform="`translate(${connection === 'external' ? 400 : 550}, 250)`">
              <circle r="35" fill="#0f172a" stroke="#10b981" stroke-width="4" filter="url(#glow-va)" />
              <text x="0" y="8" fill="#10b981" font-size="32" font-weight="900" text-anchor="middle">V</text>
              <text x="0" y="55" fill="#34d399" font-size="16" text-anchor="middle">{{ measuredU.toFixed(2) }} V</text>
              <!-- Voltmeter leading wires -->
              <path :d="connection === 'external' ? 'M 0 -115 V -35 M 0 35 V 50 H 150 V -120' : 'M 0 -115 V -35'" stroke="#475569" stroke-width="3" fill="none" />
           </g>

           <!-- Unknown Resistor Rx (待测电阻) -->
           <g transform="translate(550, 250)">
              <rect x="-30" y="-50" width="60" height="100" fill="#1e293b" stroke="#38bdf8" stroke-width="4" />
              <text x="50" y="5" fill="#38bdf8" font-size="24" font-weight="900">Rx</text>
              <text x="0" y="75" fill="#94a3b8" font-size="14" text-anchor="middle">真实值: {{ realRx }}Ω</text>
           </g>

           <!-- Direction of Current Arrows -->
           <g v-if="currentI > 0">
              <polygon points="0,0 -8,15 8,15" fill="#facc15" transform="translate(400, 100) rotate(90)" />
              <polygon points="0,0 -8,15 8,15" fill="#facc15" transform="translate(700, 300) rotate(180)" />
           </g>

        </svg>
      </div>

      <!-- 右侧解析面板 -->
      <div class="simulation-stage logic-panel">
         <div class="logic-header">伏安法电路误差解算</div>
         <div class="flex-column-center" style="flex:1; padding: 15px;">
            <div class="eq-box text-center">
               <div class="eq-display text-blue">测量数据对比</div>
               <div class="seq-data-list">
                  <div class="data-row"><span>方法类型</span><span class="hl text-yellow">{{ connection === 'internal' ? '安培表内接' : '安培表外接' }}</span></div>
                  <div class="data-row"><span>电流表读数 (I_测)</span><span class="hl text-red">{{ currentI.toFixed(4) }} A</span></div>
                  <div class="data-row"><span>电压表读数 (U_测)</span><span class="hl text-green">{{ measuredU.toFixed(2) }} V</span></div>
                  <div class="divider"></div>
                  <div class="data-row" style="background: rgba(56,189,248,0.1)">
                     <span>测量计算值 R_测</span><span class="hl text-blue">{{ measuredRx.toFixed(2) }} Ω</span>
                  </div>
                  <div class="data-row"><span>电阻真实值 R_真</span><span class="hl">{{ realRx }} Ω</span></div>
                  <div class="data-row"><span>相对误差 (%)</span><span class="hl" :class="Math.abs(errorPct) > 5 ? 'text-red' : 'text-green'">{{ errorPct.toFixed(2) }}%</span></div>
               </div>
            </div>

            <div class="eq-box mt-l">
               <p class="desc-text" style="font-size: 13px;"><strong>核心洞察：</strong><br/><br/>{{ tipDesc }}</p>
            </div>
         </div>
      </div>
    </div>

    <!-- 控制台 -->
    <div class="compact-controls">
      <div class="control-card">
         <div class="control-grid-mini">
            <div class="s-item" style="grid-column: span 2;">
               <label>电路连接方式 (关键误差来源)</label>
               <el-radio-group v-model="connection" size="small" style="display: flex; flex-wrap: wrap;">
                 <el-radio-button label="external">安培表外接法 (测小电阻)</el-radio-button>
                 <el-radio-button label="internal">安培表内接法 (测大电阻)</el-radio-button>
               </el-radio-group>
            </div>
            
            <div class="s-item">
               <label>待测电阻实值 Rx ({{ realRx }}Ω)</label>
               <el-slider v-model="realRx" :min="1" :max="500" size="small"></el-slider>
            </div>

            <div class="s-item">
               <label>变阻器 Rp 调节 (0-50Ω)</label>
               <el-slider v-model="rheostatVal" :min="0" :max="50" size="small"></el-slider>
            </div>

            <div class="s-item" style="grid-column: span 2;">
               <p style="font-size: 11px; color: #94a3b8; margin: 0;">* 假设安培表内阻 Ra = 2Ω，电压表内阻 Rv = 1000Ω</p>
            </div>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VAMethodViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      connection: 'external',
      realRx: 20,
      rheostatVal: 10,
      voltageE: 12,
      Ra: 2.0,   // Ampere internal resistance
      Rv: 1000.0 // Volt internal resistance
    }
  },
  computed: {
    topicIntro() {
       return this.connection === 'external' ? '伏安法外接法：此时电压表测得的是 Rx 的真实电压，但电流计包含了电压表支路电流。' : '伏安法内接法：此时电流计测得的是 Rx 真实电流，但电压表包含了安培表的分压。';
    },
    // Circuit calculations with non-ideal meters
    currentI() {
       const Rp = this.rheostatVal;
       const Rx = this.realRx;
       if (this.connection === 'external') {
          // Rx || Rv in series with (Ra + Rp)
          const Rparallel = (Rx * this.Rv) / (Rx + this.Rv);
          return this.voltageE / (Rp + this.Ra + Rparallel);
       } else {
          // (Rx + Ra) || Rv? No, typical simple series: Rp + Ra + Rx? 
          // Actually standard diagram: Rp series with (Rv || (Ra + Rx))
          const Rinner = (this.Rv * (this.Ra + Rx)) / (this.Rv + this.Ra + Rx);
          // Total I from battery
          const Itotal = this.voltageE / (Rp + Rinner);
          // Current through Ampere branch
          return Itotal * (this.Rv / (this.Rv + this.Ra + Rx));
       }
    },
    measuredU() {
       const Rx = this.realRx;
       if (this.connection === 'external') {
          // Voltage across parallax parallel part
          const Rparallel = (Rx * this.Rv) / (Rx + this.Rv);
          return this.currentI * Rparallel;
       } else {
          // Voltmeter is across (Ra + Rx)
          return this.currentI * (this.Ra + Rx);
       }
    },
    measuredRx() {
       return this.measuredU / this.currentI;
    },
    errorPct() {
       return ((this.measuredRx - this.realRx) / this.realRx) * 100;
    },
    tipDesc() {
       if (this.connection === 'external') return '【外接法】下，电压表的分流导致电流表读数**偏大**。所以计算出来的电阻 R = U/I 会比真实值**偏小**。当待测电阻 Rx 远小于电压表内阻 Rv 时，此误差可以忽略不计。结论：**外接法测小电阻**。';
       return '【内接法】下，安培表的分压导致电压表读数**偏大**。所以计算出来的电阻 R = U/I 会比真实值**偏大**。当待测电阻 Rx 远大于安培表内阻 Ra 时，此误差可以忽略不计。结论：**内接法测大电阻**。';
    }
  }
}
</script>

<style scoped lang="scss">
.va-method-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow-y: auto; overflow-x: hidden; padding-right: 5px; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 15px; color: #1e293b; font-weight: 700; } .formula-chip { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; padding: 8px 18px; border-radius: 6px; font-weight: 900; font-family: -apple-system, sans-serif; font-size: 18px; } }
.split-layout { display: flex; gap: 12px; flex: 1; min-height: 360px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 0.8; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; border-radius: 12px; }
.logic-header { background: #1e293b; padding: 12px; color: #38bdf8; font-size: 14px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; border-radius: 12px 12px 0 0; }
.flex-column-center { display: flex; flex-direction: column; justify-content: flex-start; overflow-y: auto; }

.eq-box { width: 100%; border-radius: 8px; overflow: hidden; }
.eq-display { background: rgba(0,0,0,0.4); padding: 10px; font-weight: 900; border-bottom: 1px solid rgba(255,255,255,0.05); }
.seq-data-list { display: flex; flex-direction: column; background: rgba(15, 23, 42, 0.6); }
.data-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 18px; border-bottom: 1px solid rgba(255,255,255,0.02); color: #94a3b8; font-size: 13px; font-weight: bold; }
.hl { color: #f8fafc; font-weight: 900; font-family: monospace; font-size: 14px; }
.divider { height: 2px; background: rgba(255,255,255,0.1); margin: 5px 0; }

.text-yellow { color: #facc15; } .text-green { color: #34d399; } .text-blue { color: #38bdf8; } .text-red { color: #f43f5e; }
.desc-text { color: #cbd5e1; font-size: 13px; line-height: 1.6; padding: 16px; background: rgba(56, 189, 248, 0.05); border-radius: 8px; border: 1px solid rgba(56, 189, 248, 0.1); }
.mt-l { margin-top: 25px; }

.simulation-stage { background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: #ffffff; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; .s-item { label { font-size: 13px; color: #475569; font-weight: 900; margin-bottom: 8px; display: block; } } }
</style>
